import { convertCurrency } from '@/helpers/convertCurrency';
import { currencyFormatter } from '@/helpers/currencyParser';
import { ProductStatus } from '@/interfaces/Planogram';
import { Product } from '@/store/Product/types';
import { ProductFieldOverrides } from '@/store/Product/useUpdateProductFields';
import {
    MarginChipLevel,
    ProductAlertInfo,
    QuickAdjustPlanogram,
} from './types';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const AVATAR_COLORS = [
    '#4b7cf3',
    '#12776e',
    '#d21e28',
    '#5b4fd6',
    '#c77b1d',
    '#0f9d8f',
    '#a83279',
    '#3f6b3f',
];

/** Margin gap (percentage points) below target that still renders as "warning" instead of "danger". */
export const MARGIN_WARNING_THRESHOLD = 0.08;

/** Products expiring within this many days trigger the "expires soon" alert tier. */
export const DUE_DATE_ALERT_MAX_DAYS = 7;

/** Minimum trimmed length for a text search (name, category, store) to be sent. */
export const MIN_SEARCH_LENGTH = 2;

/** Returns the term only when its trimmed length reaches MIN_SEARCH_LENGTH, else undefined. */
export function getSearchTerm(term: string): string | undefined {
    return term.trim().length >= MIN_SEARCH_LENGTH ? term : undefined;
}

export function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function getAvatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
        hash = name.charCodeAt(i) + (hash * 31 - hash);
    }
    const index = Math.abs(hash) % AVATAR_COLORS.length;
    return AVATAR_COLORS[index];
}

/**
 * Margem chip level: green when the practiced margin reaches the target,
 * orange when it's up to MARGIN_WARNING_THRESHOLD (8 p.p.) below the target,
 * red beyond that.
 */
export function getMarginChipLevel(
    actualMargin: number | null | undefined,
    targetMargin: number | null | undefined,
): MarginChipLevel {
    if (actualMargin == null || targetMargin == null) return 'neutral';
    const diff = actualMargin - targetMargin;

    if (diff >= 0) return 'success';
    if (diff >= -MARGIN_WARNING_THRESHOLD) return 'warning';
    return 'danger';
}

function parseDueDate(dueDate: string | Date): Date {
    if (dueDate instanceof Date) return dueDate;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dueDate);
    if (match) {
        const [, y, m, d] = match;
        return new Date(Number(y), Number(m) - 1, Number(d));
    }
    return new Date(dueDate);
}

/** Whole days between `now` and `dueDate` (positive = due date is in the future). */
export function daysUntil(
    dueDate: string | Date | null | undefined,
    now: Date = new Date(),
): number | null {
    if (!dueDate) return null;
    const due = parseDueDate(dueDate);
    if (Number.isNaN(due.getTime())) return null;

    const startOfDue = new Date(
        due.getFullYear(),
        due.getMonth(),
        due.getDate(),
    );
    const startOfNow = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
    );

    return Math.round(
        (startOfDue.getTime() - startOfNow.getTime()) / MS_PER_DAY,
    );
}

/** Formats a date-only due_date value as pt-BR without UTC/local shift. */
export function formatDueDate(
    dueDate: string | Date | null | undefined,
): string | null {
    if (!dueDate) return null;
    const due = parseDueDate(dueDate);
    if (Number.isNaN(due.getTime())) return null;
    return due.toLocaleDateString('pt-BR');
}

/** Due-date severity: 'danger' when expired, 'warning' within DUE_DATE_ALERT_MAX_DAYS days. */
export function getDueDateLevel(
    dueDate: string | Date | null | undefined,
    now: Date = new Date(),
): 'danger' | 'warning' | undefined {
    const diff = daysUntil(dueDate, now);
    if (diff === null) return undefined;
    if (diff < 0) return 'danger';
    if (diff <= DUE_DATE_ALERT_MAX_DAYS) return 'warning';
    return undefined;
}

export interface ResolveProductAlertInput {
    stockAmount: number;
    alertLevel: number;
    dueDate: string | Date | null | undefined;
    priceFinalInCents: number;
    suggestedPriceInCents: number | null | undefined;
    now?: Date;
}

/**
 * Resolves the single highest-priority alert strip for a product card, in order:
 * 1. Zero/below-alert stock (danger)
 * 2. Expired product (danger)
 * 3. Expires within DUE_DATE_ALERT_MAX_DAYS days (warning)
 * 4. App price below the suggested price (warning)
 */
export function resolveProductAlert({
    stockAmount,
    alertLevel,
    dueDate,
    priceFinalInCents,
    suggestedPriceInCents,
    now = new Date(),
}: ResolveProductAlertInput): ProductAlertInfo | null {
    if (stockAmount <= 0) {
        return { priority: 1, level: 'danger', text: 'Estoque zerado' };
    }
    if (stockAmount <= alertLevel) {
        return {
            priority: 1,
            level: 'danger',
            text: 'Estoque abaixo do nível de alerta',
        };
    }

    const dueDateLevel = getDueDateLevel(dueDate, now);
    const diffDays = daysUntil(dueDate, now);
    if (dueDateLevel === 'danger' && diffDays !== null) {
        return {
            priority: 2,
            level: 'danger',
            text: `Produto vencido há ${Math.abs(diffDays)}d`,
        };
    }

    if (dueDateLevel === 'warning' && diffDays !== null) {
        return {
            priority: 3,
            level: 'warning',
            text: diffDays === 0 ? 'Vence hoje' : `Vence em ${diffDays}d`,
        };
    }

    if (
        suggestedPriceInCents != null &&
        priceFinalInCents < suggestedPriceInCents
    ) {
        const diffInCents = suggestedPriceInCents - priceFinalInCents;
        const diffLabel = (diffInCents / 100).toFixed(2).replace('.', ',');
        return {
            priority: 4,
            level: 'warning',
            text: `Preço App R$ ${diffLabel} abaixo do sugerido`,
        };
    }

    return null;
}

/** Status chip color level: unlocked → success, lockSold → danger, lock → warning. */
export function getStatusColorLevel(
    status: string,
): 'success' | 'warning' | 'danger' {
    if (status === 'unlocked') return 'success';
    if (status === 'lockSold') return 'danger';
    return 'warning';
}

/** Preço App sugerido = último preço de compra / (1 - margem-alvo). */
export function getSuggestedAppPriceInCents(
    lastPriceInCents: number | null | undefined,
    targetMargin: number | null | undefined,
): number | null {
    if (
        !lastPriceInCents ||
        targetMargin === null ||
        targetMargin === undefined
    )
        return null;
    if (targetMargin >= 1) return null;

    return Math.round(lastPriceInCents / (1 - targetMargin));
}

/** Margem calculada = (preço app - preço de compra) / preço app. */
export function getCalculatedMargin(
    priceAppInCents: number | null | undefined,
    purchasePriceInCents: number | null | undefined,
): number | null {
    if (!priceAppInCents || !purchasePriceInCents) return null;
    return (priceAppInCents - purchasePriceInCents) / priceAppInCents;
}

/**
 * Margem-alvo: planograma → produto → categoria. Valores ausentes ou zerados
 * contam como "não cadastrada" e caem para o próximo nível.
 */
export function getTargetMargin(
    planogram: QuickAdjustPlanogram,
): number | null {
    const candidates = [
        planogram.expected_gross_margin,
        planogram.product.expected_gross_margin,
        planogram.product.Category?.expected_gross_margin,
    ];
    for (const candidate of candidates) {
        const margin = Number(candidate);
        if (candidate != null && Number.isFinite(margin) && margin > 0) {
            return margin;
        }
    }
    return null;
}

export const formatProductCount = (count: number) =>
    `${count} ${count === 1 ? 'produto' : 'produtos'}`;

export const formatCents = (value: number | null | undefined) =>
    currencyFormatter(convertCurrency('', value ?? 0));

export const getStatusLabel = (status: string) =>
    ProductStatus[status as keyof typeof ProductStatus] ?? status;

export function getApiErrorMessage(error: unknown, fallback: string): string {
    const message = (
        error as { response?: { data?: { message?: unknown } } } | undefined
    )?.response?.data?.message;
    return typeof message === 'string' && message ? message : fallback;
}

export function buildProductOverrides({
    product,
    name,
    categoryId,
}: {
    product: Product;
    name: string;
    categoryId: number | undefined;
}): ProductFieldOverrides | null {
    const overrides: ProductFieldOverrides = {};
    if (name !== product.name) overrides.name = name;
    if (categoryId !== product.Category?.id && categoryId)
        overrides.categoryId = categoryId;
    return Object.keys(overrides).length > 0 ? overrides : null;
}
